import { defineStore } from "pinia";
import { computed, reactive, ref } from "vue";
import service from "@/services/crm/dealDashboard.service";
import * as Toast from "@/utils/toast";
import { createScopedCache } from "@/stores/_cache";

const dashboardCache = createScopedCache("dashboard:deals");
const dashboardMetaCache = createScopedCache("dashboard:deals:meta");
const DEALS_DASHBOARD_TTL = 90 * 1000;
const DEALS_META_TTL = 5 * 60 * 1000;

const notifyError = (msg) => {
  if (Toast?.toast?.error) return Toast.toast.error(msg);
  if (typeof Toast?.toast === "function") return Toast.toast(msg, "error");
  if (Toast?.error) return Toast.error(msg);
  console.error(msg);
};
const notifySuccess = (msg) => {
  if (Toast?.toast?.success) return Toast.toast.success(msg);
  if (typeof Toast?.toast === "function") return Toast.toast(msg, "success");
  if (Toast?.success) return Toast.success(msg);
  console.log(msg);
};

const LS_KEY = "dq_deals_dashboard_presets_v1";

export const useDealDashboardStore = defineStore("dealDashboard", () => {
  const loading = ref(false);
  const metaLoading = ref(false);
  const exporting = ref(false);
  const error = ref(null);

  // ✅ Phase 3: auto refresh controls
  const autoRefresh = ref(true);
  const debounceMs = ref(600);

  const filters = reactive({
    q: "",
    status: "",
    pipeline_id: null,
    stage_id: null,
    owner_id: null,
    date_from: "", // yyyy-mm-dd
    date_to: "",   // yyyy-mm-dd
    limit: 5,
  });

  const meta = ref({
    pipelines: [],
    owners: [],
    statuses: ["Open", "Won", "Lost"],
  });

  const payload = ref({
    filters: {},
    kpis: {},
    pipeline_funnel: [],
    deals_by_stage: [],
    top_clients: [],
    top_deals_radar: { categories: [], series: [] },
    won_stage: { total_value_won: 0, counts: {}, percent: {} },
    recent_followups: [],
    recent_deals: [],
  });

  // ✅ Phase 3: presets
  const presets = ref([]);

  function cleanParams(obj) {
    const params = { ...obj };
    Object.keys(params).forEach((k) => {
      if (params[k] === "" || params[k] === null || params[k] === undefined) delete params[k];
    });
    return params;
  }

  function toPresetPayload(name) {
    return {
      id: `${Date.now()}`,
      name: String(name || "Preset").trim(),
      created_at: new Date().toISOString(),
      filters: {
        q: filters.q || "",
        status: filters.status || "",
        pipeline_id: filters.pipeline_id ?? null,
        stage_id: filters.stage_id ?? null,
        owner_id: filters.owner_id ?? null,
        // NOTE: date range is included (most useful for dashboards)
        date_from: filters.date_from || "",
        date_to: filters.date_to || "",
      },
    };
  }

  function loadPresets() {
    try {
      const raw = localStorage.getItem(LS_KEY);
      const list = raw ? JSON.parse(raw) : [];
      presets.value = Array.isArray(list) ? list : [];
    } catch (e) {
      presets.value = [];
    }
  }

  function persistPresets() {
    localStorage.setItem(LS_KEY, JSON.stringify(presets.value || []));
  }

  function savePreset(name) {
    const p = toPresetPayload(name);
    presets.value.unshift(p);
    // keep last 20
    presets.value = presets.value.slice(0, 20);
    persistPresets();
    notifySuccess("Preset saved");
    return p;
  }

  function deletePreset(id) {
    presets.value = (presets.value || []).filter((p) => p.id !== id);
    persistPresets();
    notifySuccess("Preset deleted");
  }

  function applyPreset(preset) {
    const f = preset?.filters || {};
    filters.q = f.q ?? "";
    filters.status = f.status ?? "";
    filters.pipeline_id = f.pipeline_id ?? null;
    filters.stage_id = f.stage_id ?? null;
    filters.owner_id = f.owner_id ?? null;
    filters.date_from = f.date_from ?? filters.date_from ?? "";
    filters.date_to = f.date_to ?? filters.date_to ?? "";
  }

  async function fetchMeta() {
    metaLoading.value = true;
    try {
      return await dashboardMetaCache.get(
        {},
        {
          ttlMs: DEALS_META_TTL,
          loader: async () => {
            const { data } = await service.meta();
            return {
              pipelines: data?.pipelines || [],
              owners: data?.owners || [],
              statuses: data?.statuses || ["Open", "Won", "Lost"],
            };
          },
          onHydrate: (payload) => {
            meta.value = payload;
          },
        },
      );
    } catch (e) {
      notifyError(e?.response?.data?.message || "Failed to load dashboard filters");
      throw e;
    } finally {
      metaLoading.value = false;
    }
  }

  async function fetchDashboard(extra = {}, options = {}) {
    loading.value = true;
    error.value = null;
    try {
      const params = cleanParams({ ...filters, ...extra });
      return await dashboardCache.get(params, {
        ttlMs: DEALS_DASHBOARD_TTL,
        force: options.force === true,
        loader: async () => {
          const { data } = await service.get(params);
          return data || {};
        },
        onHydrate: (data) => {
          payload.value = data;
          if (data?.filters) {
            filters.date_from = data.filters.date_from || filters.date_from;
            filters.date_to = data.filters.date_to || filters.date_to;
          }
        },
      });
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to load deals dashboard";
      error.value = msg;
      notifyError(msg);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  function invalidateDashboard() {
    dashboardCache.invalidate();
  }

  function invalidateMeta() {
    dashboardMetaCache.invalidate();
  }

  // ---- computed meta helpers
  const pipelines = computed(() => meta.value.pipelines || []);
  const owners = computed(() => meta.value.owners || []);
  const statuses = computed(() => meta.value.statuses || ["Open", "Won", "Lost"]);

  const selectedPipeline = computed(() => {
    const id = Number(filters.pipeline_id || 0);
    if (!id) return null;
    return pipelines.value.find((p) => Number(p.id) === id) || null;
  });

  const stageOptions = computed(() => {
    if (!selectedPipeline.value) return [];
    const stages = Array.isArray(selectedPipeline.value.stages) ? selectedPipeline.value.stages : [];
    return stages.slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  });

  // ---- payload shortcuts
  const kpis = computed(() => payload.value.kpis || {});
  const pipelineFunnel = computed(() => payload.value.pipeline_funnel || []);
  const dealsByStage = computed(() => payload.value.deals_by_stage || []);
  const topClients = computed(() => payload.value.top_clients || []);
  const topDealsRadar = computed(() => payload.value.top_deals_radar || { categories: [], series: [] });
  const wonStage = computed(() => payload.value.won_stage || { total_value_won: 0, counts: {}, percent: {} });
  const recentFollowUps = computed(() => payload.value.recent_followups || []);
  const recentDeals = computed(() => payload.value.recent_deals || []);

  // ---- export
  async function exportPdf() {
    exporting.value = true;
    try {
      const params = cleanParams({ ...filters });
      return await service.exportPdf(params);
    } finally {
      exporting.value = false;
    }
  }

  async function exportExcel() {
    exporting.value = true;
    try {
      const params = cleanParams({ ...filters });
      return await service.exportExcel(params);
    } finally {
      exporting.value = false;
    }
  }

  function resetFilters({ keepDates = true } = {}) {
    const df = filters.date_from;
    const dt = filters.date_to;
    filters.q = "";
    filters.status = "";
    filters.pipeline_id = null;
    filters.stage_id = null;
    filters.owner_id = null;
    filters.limit = 5;
    if (keepDates) {
      filters.date_from = df;
      filters.date_to = dt;
    } else {
      filters.date_from = "";
      filters.date_to = "";
    }
  }

  // ✅ helper: set stage by name (for chart clicks)
  function setStageByName(stageName) {
    if (!stageName) return false;

    // if pipeline selected => use its stageOptions
    if (selectedPipeline.value) {
      const hit = stageOptions.value.find(
        (s) => String(s.name || "").toLowerCase() === String(stageName).toLowerCase()
      );
      if (hit) {
        filters.stage_id = hit.id;
        return true;
      }
    }

    // else search across all pipelines
    for (const p of pipelines.value) {
      const stages = Array.isArray(p.stages) ? p.stages : [];
      const hit = stages.find(
        (s) => String(s.name || "").toLowerCase() === String(stageName).toLowerCase()
      );
      if (hit) {
        filters.pipeline_id = p.id;
        filters.stage_id = hit.id;
        return true;
      }
    }
    return false;
  }

  // init presets on store creation
  loadPresets();

  return {
    loading,
    metaLoading,
    exporting,
    error,

    autoRefresh,
    debounceMs,

    filters,
    meta,

    presets,
    loadPresets,
    savePreset,
    deletePreset,
    applyPreset,

    fetchMeta,
    fetchDashboard,
    invalidateDashboard,
    invalidateMeta,

    pipelines,
    owners,
    statuses,
    selectedPipeline,
    stageOptions,

    kpis,
    pipelineFunnel,
    dealsByStage,
    topClients,
    topDealsRadar,
    wonStage,
    recentFollowUps,
    recentDeals,

    exportPdf,
    exportExcel,

    resetFilters,
    setStageByName,
  };
});
